import { useRef, useState } from "react";
import { Loader2, Mic, Square, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";

export const VoiceRecorder = ({ transcription, setTranscription }) => {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const stop = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
    clearInterval(timerRef.current);
    setRecording(false);
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];
      const rec = new MediaRecorder(stream);
      recorderRef.current = rec;
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (!blob.size) return;
        setBusy(true);
        const form = new FormData();
        form.append("audio", blob, "message.webm");
        try {
          const { data } = await api.post("/transcribe", form, { headers: { "Content-Type": "multipart/form-data" } });
          setTranscription(data.transcription);
          toast.success("Transcription générée — relisez-la avant envoi");
        } catch (e) {
          toast.error(errMsg(e, "Transcription impossible"));
        } finally {
          setBusy(false);
        }
      };
      rec.start();
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          if (s >= 119) stop();
          return s + 1;
        });
      }, 1000);
    } catch {
      toast.error("Micro inaccessible sur cet appareil");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        {!recording ? (
          <Button data-testid="voice-record-btn" type="button" onClick={start} disabled={busy} variant="outline" className="h-12 rounded-xl border-slate-300">
            {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : <Mic size={16} className="mr-2" />}
            {busy ? "Transcription…" : "Message vocal"}
          </Button>
        ) : (
          <Button data-testid="voice-stop-btn" type="button" onClick={stop} className="h-12 rounded-xl bg-red-500 hover:bg-red-600 bk-pulse">
            <Square size={14} className="mr-2" /> Arrêter · {String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}
          </Button>
        )}
        <span className="text-xs text-slate-500">2 min max — transcrit par IA, éditable</span>
      </div>

      {transcription !== "" && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="bk-label">Transcription à valider</div>
            <button type="button" data-testid="voice-clear-btn" onClick={() => setTranscription("")} className="text-slate-400 hover:text-red-500">
              <Trash2 size={15} />
            </button>
          </div>
          <Textarea data-testid="voice-transcription-textarea" value={transcription} onChange={(e) => setTranscription(e.target.value)} rows={4} />
        </div>
      )}
    </div>
  );
};

export default VoiceRecorder;
